/**
 * @file The form route's page: Navb, the form content and the import DropHint.
 * Consumer: the router. At `md` and wider, `FlexPage` and `FlexContent` take
 * `width: 'max-content'`: ArTable's sticky header and first column ride document
 * scroll, and `arTableFullScreenStore` restores `window.scrollX`. Below `md` the
 * page is held to the viewport and each ArTable scrolls in its own box, so a
 * phone's layout viewport never grows past the device width. The Navb carries
 * `contain: inline-size`, so its menu never sets the page width. Its measured box
 * is published as `--navb-height`/`--navb-width` on the `FlexPage` for the sticky
 * form header; until measured, fallbacks apply.
 */
import { CSSProperties, FC } from 'react';
import { Grid } from 'antd';
import Navb from '../components/Navb';
import FlexPage from '../components/atoms/FlexPage';
import FlexContent from '../components/atoms/FlexContent';
import { FORM_PAGE_CSS_VARS } from '../components/atoms/FORM_PAGE_CSS_VARS';
import { useElementBoxSize } from '../use/useElementBoxSize';
import { DropHint } from '../components/extract';
import useFormImportDrop from '../use/useFormImportDrop';
import { FormPageContent } from './FormPageContent';

const MAX_CONTENT = { width: 'max-content' } as const;

const NAVB_STYLE = { contain: 'inline-size' } as const;

type FormPageStyle = CSSProperties &
  Partial<Record<'--navb-height' | '--navb-width', string>>;

const FormPage: FC = () => {
  const {
    dropDisabled,
    pdfsOpenInIntake,
    validDropExtensions,
    handleDrop,
    handleFileBrowse,
  } = useFormImportDrop();
  const [navbRef, navbSize] = useElementBoxSize<HTMLDivElement>();
  const { md } = Grid.useBreakpoint();
  const pageWidth = md ? MAX_CONTENT : undefined;

  const pageStyle: FormPageStyle = {
    ...pageWidth,
    ...(navbSize && {
      [FORM_PAGE_CSS_VARS.navbHeight]: `${navbSize.height}px`,
      [FORM_PAGE_CSS_VARS.navbWidth]: `${navbSize.width}px`,
    }),
  };

  return (
    <FlexPage style={pageStyle}>
      <Navb ref={navbRef} style={NAVB_STYLE} />
      <FlexContent
        style={pageWidth}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        <FormPageContent />
      </FlexContent>
      <DropHint
        onDrop={handleDrop}
        onFileBrowse={handleFileBrowse}
        validExtensions={validDropExtensions}
        importDisabled={dropDisabled}
        pdfsOpenInIntake={pdfsOpenInIntake}
      />
    </FlexPage>
  );
};

export default FormPage;
